import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommunities } from '../api/intelligence';

const TYPE_COLORS: Record<string, string> = {
  person: 'bg-blue-500/20 text-blue-400',
  organization: 'bg-purple-500/20 text-purple-400',
  location: 'bg-green-500/20 text-green-400',
  vehicle: 'bg-orange-500/20 text-orange-400',
  financial_account: 'bg-yellow-500/20 text-yellow-400',
};

export default function CommunitiesPage() {
  const navigate = useNavigate();
  const [algorithm, setAlgorithm] = useState('label_propagation');
  const [minSize, setMinSize] = useState(2);
  const { data, isLoading } = useCommunities(algorithm, minSize);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Detection de communautes</h1>
          <p className="text-sm text-gray-500 mt-1">Groupes d'entites fortement connectees</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={algorithm}
            onChange={(e) => setAlgorithm(e.target.value)}
            className="px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
          >
            <option value="label_propagation">Label Propagation</option>
            <option value="connected_components">Composants connexes</option>
          </select>
          <select
            value={minSize}
            onChange={(e) => setMinSize(Number(e.target.value))}
            className="px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
          >
            <option value={2}>Min 2 membres</option>
            <option value={3}>Min 3 membres</option>
            <option value={5}>Min 5 membres</option>
            <option value={10}>Min 10 membres</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      {data && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Communautes', value: data.total_communities },
            { label: 'Entites', value: data.total_entities },
            { label: 'Relations', value: data.total_relationships },
            { label: 'Isolees', value: data.isolated_entities },
          ].map((s) => (
            <div key={s.label} className="glass-panel p-4 text-center">
              <p className="text-2xl font-bold text-cyber-cyan">{s.value}</p>
              <p className="text-xs text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
        </div>
      )}

      {/* Communities list */}
      <div className="space-y-4">
        {data?.communities.map((community) => (
          <div key={community.id} className="glass-panel p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyber-cyan/10 flex items-center justify-center">
                  <span className="text-lg font-bold text-cyber-cyan">#{community.id}</span>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-200">
                    Communaute {community.id} — {community.size} membres
                  </h3>
                  {community.hub_entity && (
                    <p className="text-xs text-gray-500">
                      Hub: {community.hub_entity.entity_name} ({community.hub_entity.degree} connexions)
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">Densite: {(community.density * 100).toFixed(0)}%</span>
                <div className="flex gap-1">
                  {Object.entries(community.entity_types).map(([type, count]) => (
                    <span key={type} className={`px-2 py-0.5 text-xs rounded-full ${TYPE_COLORS[type] || 'bg-gray-500/20 text-gray-400'}`}>
                      {count} {type}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {community.members.slice(0, 20).map((m) => (
                <button
                  key={m.entity_id}
                  onClick={() => navigate(`/entities/${m.entity_id}`)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-tertiary/50 hover:bg-surface-tertiary transition-colors text-left"
                >
                  <div className={`w-7 h-7 rounded flex items-center justify-center text-xs font-bold ${TYPE_COLORS[m.entity_type] || 'bg-gray-500/20 text-gray-400'}`}>
                    {m.entity_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-300 truncate">{m.entity_name}</p>
                    <p className="text-xs text-gray-600">{m.degree} liens</p>
                  </div>
                </button>
              ))}
              {community.members.length > 20 && (
                <div className="flex items-center justify-center px-3 py-2 rounded-lg bg-surface-tertiary/30 text-xs text-gray-500">
                  +{community.members.length - 20} autres
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {!isLoading && data && data.communities.length === 0 && (
        <p className="text-center text-gray-500 py-12">Aucune communaute detectee avec ces parametres</p>
      )}
    </div>
  );
}
